import type { DrawingEntity, Matrix3, Point } from '../contract/types.js';
import { applyTo, normaliseDegrees } from './matrix.js';

/**
 * Moving entities from page space into drawing space.
 *
 * Entities are held in PDF points throughout, with the origin at the top left
 * and y increasing downward. That is deliberate: it keeps the overlay aligned
 * to the original page and lets any mark be traced back to its source. The
 * transform to drawing millimetres is *stored*, and applied only at the moment
 * a DXF is written.
 *
 * The subtlety is that the transform reverses orientation. Handling that wrong
 * is the classic failure of this kind of work, and it fails silently:
 *
 *  - Arc angles negate **and swap**, because reflection reverses direction
 *    while DXF arcs are always counter-clockwise about the extrusion vector.
 *  - Polyline bulge signs negate for the same reason.
 *  - Text must be placed by computing the flipped baseline point and setting
 *    the rotation directly. Mirroring a text transform cannot be repaired
 *    afterwards and returns no error when it fails.
 */

/** Uniform scale factor of an affine matrix. */
export function scaleFactorOf(m: Matrix3): number {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));
}

/** True when the matrix reverses orientation. */
export function reversesOrientation(m: Matrix3): boolean {
  return m[0] * m[3] - m[1] * m[2] < 0;
}

export function toDrawingSpace(entity: DrawingEntity, m: Matrix3): DrawingEntity {
  const k = scaleFactorOf(m);
  const flipped = reversesOrientation(m);

  switch (entity.type) {
    case 'LINE':
      return { ...entity, start: applyTo(m, entity.start), end: applyTo(m, entity.end) };

    case 'LWPOLYLINE':
      return {
        ...entity,
        vertices: entity.vertices.map((v) => applyTo(m, v)),
        bulges: flipped ? entity.bulges.map((b) => -b) : [...entity.bulges],
      };

    case 'CIRCLE':
      return { ...entity, centre: applyTo(m, entity.centre), radius: entity.radius * k };

    case 'ARC': {
      // Transform the arc's endpoints rather than trying to transform the angles
      // directly. This is correct for any conformal matrix, including ones that
      // rotate as well as flip, and it cannot get the negate-and-swap wrong.
      const centre = applyTo(m, entity.centre);
      const start = applyTo(m, pointOnCircle(entity.centre, entity.radius, entity.startAngle));
      const end = applyTo(m, pointOnCircle(entity.centre, entity.radius, entity.endAngle));

      const a0 = angleOf(centre, start);
      const a1 = angleOf(centre, end);

      return {
        ...entity,
        centre,
        radius: entity.radius * k,
        // Reversing orientation reverses the direction of travel, so what was
        // the start becomes the end.
        startAngle: flipped ? a1 : a0,
        endAngle: flipped ? a0 : a1,
      };
    }

    case 'TEXT':
    case 'MTEXT': {
      const anchor = applyTo(m, entity.anchor);
      // The stored rotation is the angle as it appears on the page. Transform a
      // direction vector rather than the angle so the result is right for any
      // matrix, and read the rotation back from it.
      const rad = (entity.rotation * Math.PI) / 180;
      const tip = applyTo(m, {
        x: entity.anchor.x + Math.cos(rad),
        y: entity.anchor.y - Math.sin(rad),
      });
      return {
        ...entity,
        anchor,
        height: entity.height * k,
        /*
          The other two lengths on a text entity, scaled with it.

          `lineWidths` and `linePitch` are documented as being in the same units
          as `height`, and `height` is multiplied here while both of those used to
          ride through the spread untouched. Nothing consumed them after a move --
          the overlay draws in page space and the writer takes its ratios from the
          unmoved entity -- so a moved entity quietly carried page points beside
          millimetres, waiting for the first consumer to compare the two.
        */
        ...(entity.lineWidths !== undefined
          ? { lineWidths: entity.lineWidths.map((w) => w * k) }
          : {}),
        ...(entity.linePitch !== undefined ? { linePitch: entity.linePitch * k } : {}),
        rotation: normaliseDegrees((Math.atan2(tip.y - anchor.y, tip.x - anchor.x) * 180) / Math.PI),
      };
    }
  }
}

function pointOnCircle(centre: Point, radius: number, angleDeg: number): Point {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: centre.x + radius * Math.cos(rad), y: centre.y + radius * Math.sin(rad) };
}

function angleOf(centre: Point, p: Point): number {
  return normaliseDegrees((Math.atan2(p.y - centre.y, p.x - centre.x) * 180) / Math.PI);
}
